// OlympiadSettings.tsx
import React, { useState, useEffect } from 'react';
import {
    Card,
    Typography,
    Form,
    Input,
    DatePicker,
    Button,
    List,
    Modal,
    message,
    Row,
    Col,
    Space,
    InputNumber,
    Tag
} from 'antd';
import {
    SaveOutlined,
    CloseOutlined,
    PlusOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import "./OlympiadSettings.less"
import { postRequest } from '../../../../../api/httpRequest';
import { 
    OlympiadSettingsProps, 
    OlympiadTask,
    SaveTaskData
} from './types';
import TaskItem from './TaskItem';
import TaskModal from './TaskModal';
import SettingsCard from './SettingsCard';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const OlympiadSettings: React.FC<OlympiadSettingsProps> = ({ 
    olympiadData, 
    passingScore, 
    onPassingScoreChange 
}) => {
    const [form] = Form.useForm();
    const [tasks, setTasks] = useState<OlympiadTask[]>([]);
    const [originalTasks, setOriginalTasks] = useState<OlympiadTask[]>([]);
    const [deletedTasks, setDeletedTasks] = useState<number[]>([]);
    const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
    const [editingTask, setEditingTask] = useState<OlympiadTask | null>(null);
    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const getTaskStatus = (task: OlympiadTask) => {
        if (task.id === '') return 'added';
        
        if (typeof task.id === 'number' && deletedTasks.includes(task.id)) return 'deleted';
        
        const originalTask = originalTasks.find(t => t.id === task.id);
        
        if (!originalTask) return 'added';
        if (originalTask.question !== task.question || 
            originalTask.answer !== task.answer ||
            originalTask.imageId !== task.imageId) {
            return 'modified';
        }
        return null;
    };

    const getStatusTag = (task: OlympiadTask) => {
        const status = getTaskStatus(task);
        
        if (status === 'added') {
            return <Tag color="green">Добавлено</Tag>;
        }
        if (status === 'modified') {
            return <Tag color="blue">Изменено</Tag>;
        }
        if (status === 'deleted') {
            return <Tag color="red">К удалению</Tag>;
        }
        return null;
    };

    const checkForChanges = () => {
        const hasTaskChanges = 
            deletedTasks.length > 0 || 
            tasks.some(task => getTaskStatus(task) === 'added' || getTaskStatus(task) === 'modified');
        
        setHasChanges(hasTaskChanges);
    };

    useEffect(() => {
        if (olympiadData) {
            const formattedData = {
                id: olympiadData.id.toString(),
                discipline: olympiadData.discipline,
                period: olympiadData.period ? [
                    dayjs(olympiadData.period.split(' - ')[0]),
                    dayjs(olympiadData.period.split(' - ')[1])
                ] : [dayjs(), dayjs().add(1, 'month')],
                dateEnd: olympiadData.dateEnd ? dayjs(olympiadData.dateEnd) : dayjs(),
                grade: olympiadData.grade || '',
                passingScore: parseInt(olympiadData.passingScore) || 0,
                secondRoundPassingScore: parseInt(olympiadData.secondRoundPassingScore) || 0
            };

            form.setFieldsValue(formattedData);
            
            const formattedTasks = olympiadData.tasks?.map((task: any) => ({
                id: parseInt(task.id),
                question: task.question,
                answer: task.answer,
                imageId: task.imageId, // Используем imageId вместо fileUrl
                fileName: task.fileName
            })) || [];
            
            setTasks(formattedTasks);
            setOriginalTasks(formattedTasks);
            setDeletedTasks([]);
        }
    }, [olympiadData, form]);

    useEffect(() => {
        checkForChanges();
    }, [tasks, deletedTasks]);

    const handleSaveSettings = async (values: any) => {
        setLoading(true);
        try {
            // Подготавливаем задачи для сохранения
            const tasksToSave: SaveTaskData[] = tasks
                .filter(task => {
                    if (typeof task.id === 'number') {
                        return !deletedTasks.includes(task.id);
                    }
                    return true;
                })
                .map(task => ({
                    id: task.id === '' ? '' : task.id.toString(),
                    question: task.question,
                    answer: task.answer,
                    imageId: task.imageId, // Сохраняем идентификатор изображения
                    fileName: task.fileName
                }));

            const dataToSave = {
                id: olympiadData.id,
                discipline: values.discipline,
                period: values.period ? `${values.period[0].format('YYYY-MM-DD')} - ${values.period[1].format('YYYY-MM-DD')}` : '',
                dateEnd: values.dateEnd ? values.dateEnd.format('YYYY-MM-DD') : '',
                grade: values.grade,
                passingScore: values.passingScore,
                secondRoundPassingScore: values.secondRoundPassingScore,
                tasks: tasksToSave,
                deletedTasks: deletedTasks
            };

            postRequest('/server/olympiad/teacher/save', dataToSave)
            .then(resp => {
                if (resp && resp.data.tasks) {
                    const updatedTasks = resp.data.tasks.map((task: any) => ({
                        id: parseInt(task.id),
                        question: task.question,
                        answer: task.answer,
                        imageId: task.imageId, // Сохраняем imageId из ответа сервера
                        fileName: task.fileName
                    }));
                    setTasks(updatedTasks);
                    setOriginalTasks(updatedTasks);
                } else {
                    const updatedTasks = tasks
                        .filter(task => {
                            if (typeof task.id === 'number') {
                                return !deletedTasks.includes(task.id);
                            }
                            return true;
                        });
                    
                    setTasks(updatedTasks);
                    setOriginalTasks(updatedTasks);
                }
                
                setDeletedTasks([]);
                setHasChanges(false);
                
                message.success('Настройки и задания успешно сохранены');
                onPassingScoreChange(values.passingScore);
            })
            .catch(error => {
                message.error('Ошибка при сохранении настроек');
            });

        } catch (error) {
            message.error('Ошибка при сохранении настроек');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelChanges = () => {
        setTasks([...originalTasks]);
        setDeletedTasks([]);
        setHasChanges(false);
        message.info('Изменения отменены');
    };

    const handleAddTask = () => {
        setEditingTask(null);
        setIsTaskModalVisible(true);
    };

    const handleEditTask = (task: OlympiadTask) => {
        if (typeof task.id === 'number' && deletedTasks.includes(task.id)) {
            setDeletedTasks(prev => prev.filter(id => id !== task.id));
            message.info('Задание восстановлено');
            return;
        }
        setEditingTask(task);
        setIsTaskModalVisible(true);
    };

    const handleDeleteTask = (taskId: number | string) => {
        if (taskId === '') {
            Modal.confirm({
                title: 'Удалить задание?',
                content: 'Вы уверены, что хотите удалить это задание?',
                okText: 'Удалить',
                cancelText: 'Отмена',
                onOk: () => {
                    setTasks(prev => prev.filter(task => task.id !== taskId));
                    message.success('Задание удалено');
                }
            });
            return;
        }

        if (typeof taskId === 'number') {
            const isNewTask = !originalTasks.some(t => t.id === taskId);
            
            if (isNewTask) {
                Modal.confirm({
                    title: 'Удалить задание?',
                    content: 'Вы уверены, что хотите удалить это задание?',
                    okText: 'Удалить',
                    cancelText: 'Отмена',
                    onOk: () => {
                        setTasks(prev => prev.filter(task => task.id !== taskId));
                        message.success('Задание удалено');
                    }
                });
            } else {
                setDeletedTasks(prev => [...prev, taskId]);
                message.success('Задание помечено к удалению');
            }
        }
    };

    const handleSaveTask = (values: any) => {
        if (editingTask) {
            setTasks(prev => prev.map(task =>
                task.id === editingTask.id
                    ? { 
                        ...task, 
                        question: values.question,
                        answer: values.answer,
                        imageId: values.imageId,
                        fileName: values.fileName
                    }
                    : task
            ));
            message.success('Задание обновлено');
        } else {
            const newTask: OlympiadTask = {
                id: '',
                question: values.question,
                answer: values.answer,
                imageId: values.imageId,
                fileName: values.fileName
            };
            setTasks(prev => [...prev, newTask]);
            message.success('Задание добавлено');
        }
        setIsTaskModalVisible(false);
        setEditingTask(null); // Добавить сброс editingTask
    };

    return (
        <div className="olympiad-settings">
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSaveSettings}
                size="small"
                className="settings-form"
            >
                <Space direction="vertical" size="middle" className="settings-space">
                    <Card size="small" className="actions-card">
                        <div className="actions-content">
                            <Space size="middle" align="center">
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SaveOutlined />}
                                    loading={loading}
                                    size="small"
                                    className="save-button"
                                >
                                    Сохранить все настройки
                                </Button>
                                
                                <Button
                                    type="default"
                                    icon={<CloseOutlined />}
                                    onClick={handleCancelChanges}
                                    disabled={!hasChanges}
                                    size="small"
                                    className="cancel-button"
                                >
                                    Отменить изменения
                                </Button>
                                
                                <Text type="secondary" className="actions-note">
                                    При изменении заданий нужно нажать кнопку сохранения
                                </Text>
                            </Space>
                        </div>
                    </Card>

                    <Row gutter={16} className="settings-row">
                        <Col xs={24} lg={12}>
                            <SettingsCard title="Основные настройки">
                                <Form.Item
                                    name="discipline"
                                    label="Дисциплина"
                                    rules={[{ required: true, message: 'Введите дисциплину' }]}
                                >
                                    <Input placeholder="Дисциплина олимпиады" />
                                </Form.Item>

                                <Form.Item
                                    name="grade"
                                    label="Класс"
                                >
                                    <Input placeholder="Класс" />
                                </Form.Item>

                                <Form.Item
                                    name="dateEnd"
                                    label="Дата окончания записи"
                                    rules={[{ required: true, message: 'Выберите дату' }]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        placeholder="Дата окончания записи"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="period"
                                    label="Период проведения"
                                    rules={[{ required: true, message: 'Выберите период' }]}
                                >
                                    <RangePicker
                                        style={{ width: '100%' }}
                                        placeholder={['Начало', 'Окончание']}
                                    />
                                </Form.Item>
                            </SettingsCard>
                        </Col>
                        
                        <Col xs={24} lg={12}>
                            <SettingsCard title="Настройка проходных баллов">
                                <Form.Item
                                    name="passingScore"
                                    label="Проходной балл для первого тура"
                                    rules={[{ required: true, message: 'Введите проходной балл' }]}
                                >
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        style={{ width: '100%' }}
                                        placeholder="Проходной балл для первого тура"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="secondRoundPassingScore"
                                    label="Проходной балл для второго тура"
                                    rules={[{ required: true, message: 'Введите проходной балл' }]}
                                >
                                    <InputNumber
                                        min={0}
                                        max={100}
                                        style={{ width: '100%' }}
                                        placeholder="Проходной балл для второго тура"
                                    />
                                </Form.Item>
                            </SettingsCard>
                        </Col>
                    </Row>

                    <Card size="small" className="settings-card">
                        <div className="tasks-header">
                            <Title level={5} className="tasks-title">
                                Задания олимпиады
                            </Title>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAddTask}
                                size="small"
                                className="add-task-button"
                            >
                                Добавить задание
                            </Button>
                        </div>

                        <List
                            dataSource={tasks}
                            renderItem={(task) => (
                                <TaskItem
                                    task={task}
                                    statusTag={getStatusTag(task)}
                                    onEdit={handleEditTask}
                                    onDelete={handleDeleteTask}
                                    isDeleted={getTaskStatus(task) === 'deleted'}
                                />
                            )}
                            locale={{ emptyText: <div className="empty-text">Задания еще не добавлены</div> }}
                            className="tasks-list"
                        />
                    </Card>
                </Space>
            </Form>

            <TaskModal
                visible={isTaskModalVisible}
                editingTask={editingTask}
                onSave={handleSaveTask}
                onCancel={() => setIsTaskModalVisible(false)}
            />
        </div>
    );
};

export default OlympiadSettings;